import type { ItemType } from '@brickloop/core/schemas';
import { useEffect, useRef, useState } from 'react';
import { api, type Color, type RecognitionResponse } from '../api';
import { preparePhoto, type Photo } from '../lib/photo';
import { useRecognitionStatus } from '../queries';
import { Badge, Button, Card, ErrorMessage, Field, Input, Select, Spinner, cn } from '../ui';
import { ItemImage } from './ItemImage';

export interface ScanPick {
  itemType: ItemType;
  itemNo: string;
  /** Only worth passing on when the item is not in the catalogue yet, so it can be created. */
  itemName?: string;
  /** The name to show: the catalogue's when it is there, else what Brickognize called it. */
  displayName: string;
  colorName: string;
  qty: number;
}

type State =
  | { phase: 'idle' }
  | { phase: 'working' }
  | { phase: 'live' }
  | { phase: 'error'; message: string }
  | { phase: 'done'; photo: Photo; result: RecognitionResponse };

/** A phone or tablet has its own camera app, which beats a viewfinder in the page. */
const isTouch = () => typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0 && /Android|iPhone|iPad|Mobile/i.test(navigator.userAgent);

const TYPE_WORD: Record<string, string> = { PART: 'part', SET: 'set', MINIFIG: 'minifigure' };

/**
 * Take a photo of a part, see what it might be, tap the right one and a colour, and it goes into the rows.
 *
 * It uses a plain file input with the camera attribute, which opens the phone's own camera app. That works
 * everywhere, including over plain HTTP on a local network, where a live in-page viewfinder would be refused.
 * Nothing shows at all unless the owner has switched recognition on.
 */
export function CameraScan({ colors, onAdd, identifyOnly = false }: { colors: Color[]; onAdd: (pick: ScanPick) => void; /** Just name the part (no quantity, no scanning on to the next), for filling in a form. */ identifyOnly?: boolean }) {
  const status = useRecognitionStatus();
  const input = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<State>({ phase: 'idle' });
  const [picked, setPicked] = useState(0);
  const [colourId, setColourId] = useState<number | null>(null);
  const [qty, setQty] = useState('1');
  // A live viewfinder for a laptop webcam. Browsers allow it on localhost and over HTTPS, not plain HTTP on a network.
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const canWebcam = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && window.isSecureContext;
  const stopWebcam = () => {
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
  };
  useEffect(() => stopWebcam, []);
  async function startWebcam() {
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 960 } } });
      setState({ phase: 'live' });
    } catch (err) {
      setState({ phase: 'error', message: err instanceof DOMException && err.name === 'NotAllowedError' ? 'The browser was not allowed to use the camera. Allow it in the address bar and try again.' : 'No camera could be started on this computer.' });
    }
  }
  // The video element only exists once the phase is live, so attach the stream then.
  useEffect(() => {
    if (state.phase === 'live' && video.current && stream.current) void (video.current.srcObject = stream.current);
  }, [state.phase]);
  function capture() {
    const v = video.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext('2d')?.drawImage(v, 0, 0);
    stopWebcam();
    canvas.toBlob((blob) => {
      if (blob) void handleFile(new File([blob], 'webcam.jpg', { type: 'image/jpeg' }));
      else setState({ phase: 'error', message: 'Could not take the picture.' });
    }, 'image/jpeg', 0.9);
  }

  // Let go of the local picture address when it is replaced or the card goes away.
  useEffect(
    () => () => {
      if (state.phase === 'done') URL.revokeObjectURL(state.photo.previewUrl);
    },
    [state],
  );

  async function handleFile(file: File) {
    setState({ phase: 'working' });
    try {
      const photo = await preparePhoto(file);
      const result = await api<RecognitionResponse>('/api/v1/recognition/identify', {
        body: { image: photo.base64, mime: photo.mime },
      });
      setPicked(0);
      setColourId(null);
      setQty('1');
      setState({ phase: 'done', photo, result });
    } catch (err) {
      setState({ phase: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }

  if (!status.data?.enabled) return null;

  const openCamera = () => (canWebcam && !isTouch() ? void startWebcam() : input.current?.click());
  const reset = () => {
    stopWebcam();
    setState({ phase: 'idle' });
  };

  const done = state.phase === 'done' ? state : null;
  const candidate = done?.result.candidates[picked];
  const needsColour = candidate?.itemType === 'PART';

  // Colours Brickognize itself suggested, matched to ours by name (their ids are not documented).
  const providerChips =
    done?.result.providerColours.flatMap((pc) => {
      const c = colors.find((x) => x.name.toLowerCase() === pc.name.toLowerCase());
      return c ? [{ color: c, score: pc.score }] : [];
    }) ?? [];

  const chosenColourId = colourId ?? providerChips[0]?.color.id ?? null;
  const chosenColour = colors.find((c) => c.id === chosenColourId) ?? null;
  const qtyNumber = Number(qty);
  const canAdd = !!candidate && Number.isInteger(qtyNumber) && qtyNumber >= 1 && (!needsColour || !!chosenColour);

  function add(scanNext: boolean) {
    if (!candidate || !canAdd) return;
    onAdd({
      itemType: candidate.itemType,
      itemNo: candidate.itemNo,
      itemName: candidate.item ? undefined : candidate.name,
      displayName: candidate.item?.name ?? candidate.name,
      colorName: needsColour ? (chosenColour?.name ?? '') : '',
      qty: qtyNumber,
    });
    reset();
    if (scanNext) openCamera();
  }

  return (
    <Card title="Scan with the camera">
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        aria-label="Take a photo of a part"
        tabIndex={-1}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = ''; // so taking the same photo again still counts as a change
          if (file) void handleFile(file);
        }}
      />

      {state.phase === 'idle' && (
        <div className="flex flex-wrap items-center gap-4">
          <Button large className="w-full sm:w-auto" onClick={openCamera}>
            Take a photo
          </Button>
          <p className="max-w-md text-sm text-slate-600">
            One part at a time, on a plain background, filling the frame. On a phone this opens the camera; on a laptop it uses the webcam.
          </p>
          {canWebcam && isTouch() === false && (
            <Button variant="ghost" onClick={() => input.current?.click()}>
              Choose a photo file instead
            </Button>
          )}
        </div>
      )}

      {state.phase === 'live' && (
        <div className="space-y-3">
          <video ref={video} autoPlay playsInline muted className="max-h-80 w-auto max-w-full rounded-md bg-black" />
          <div className="flex gap-3">
            <Button large onClick={capture}>
              Take the picture
            </Button>
            <Button large variant="secondary" onClick={reset}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {state.phase === 'working' && <Spinner label="Looking at your photo…" />}

      {state.phase === 'error' && (
        <div className="space-y-3">
          <ErrorMessage error={new Error(state.message)} />
          <Button large onClick={openCamera}>
            Try again
          </Button>
        </div>
      )}

      {done && (
        <div className="grid grid-cols-[minmax(0,1fr)] gap-5 md:grid-cols-[minmax(0,16rem)_minmax(0,1fr)]">
          <div>
            <div className="relative inline-block">
              <img src={done.photo.previewUrl} alt="The photo you took" className="block max-h-36 w-auto max-w-full rounded-md sm:max-h-64" />
              {done.result.box && (
                <div
                  aria-hidden="true"
                  className="absolute rounded border-2 border-brand shadow-[0_0_0_1px_rgba(0,0,0,0.4)]"
                  style={{
                    left: `${(done.result.box.left / done.result.box.imageWidth) * 100}%`,
                    top: `${(done.result.box.upper / done.result.box.imageHeight) * 100}%`,
                    width: `${((done.result.box.right - done.result.box.left) / done.result.box.imageWidth) * 100}%`,
                    height: `${((done.result.box.lower - done.result.box.upper) / done.result.box.imageHeight) * 100}%`,
                  }}
                />
              )}
            </div>
          </div>

          <div className="min-w-0 space-y-4">
            {done.result.candidates.length === 0 ? (
              <p className="text-sm text-slate-700">
                Nothing was recognised. Try again with the part filling more of the frame, on a plain background, in good light.
              </p>
            ) : (
              <>
                <fieldset className="min-w-0">
                  <legend className="mb-1 text-sm font-medium text-slate-700">Which is it?</legend>
                  <ul className="space-y-1.5">
                    {done.result.candidates.slice(0, 5).map((c, i) => (
                      <li key={`${c.itemType}-${c.itemNo}-${i}`}>
                        <label
                          className={cn(
                            'flex min-h-12 cursor-pointer items-center gap-3 rounded-lg border p-2',
                            picked === i ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:bg-slate-50',
                          )}
                        >
                          <input type="radio" name="scan-candidate" checked={picked === i} onChange={() => setPicked(i)} className="size-4" />
                          <ItemImage itemType={c.itemType} itemNo={c.itemNo} colorId={chosenColour?.id ?? 0} />
                          <span className="min-w-0 flex-1">
                            <span className="block line-clamp-2 text-sm font-medium">
                              {c.itemNo} <span className="font-normal text-slate-600">{c.item?.name ?? c.name}</span>
                            </span>
                            <span className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                              <span>{Math.round(c.score * 100)}% sure</span>
                              <span>· {TYPE_WORD[c.itemType] ?? c.itemType.toLowerCase()}</span>
                              {c.stock.pieces > 0 ? <Badge tone="green">you have {c.stock.pieces}</Badge> : c.item ? <Badge>none in stock</Badge> : null}
                              {!c.item && <Badge tone="amber">not in your catalogue</Badge>}
                            </span>
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                  {candidate && !candidate.item && (
                    <p className="mt-1 text-xs break-words text-amber-800">
                      {candidate.idSource === 'provider-id'
                        ? 'This number comes from Brickognize and has not been checked against BrickLink. Check it before adding.'
                        : 'It will be added to your catalogue under the name shown.'}
                    </p>
                  )}
                </fieldset>

                {needsColour && (
                  <fieldset className="min-w-0">
                    <legend className="mb-1 text-sm font-medium text-slate-700">Colour</legend>
                    <div className="flex flex-wrap gap-2">
                      {providerChips
                        .map((p) => ({ color: p.color, note: `${Math.round(p.score * 100)}%`, tone: 'maybe' as const }))
                        .filter((x) => x.color)
                        .slice(0, 6)
                        .map(({ color, note, tone }) => (
                          <button
                            key={color.id}
                            type="button"
                            onClick={() => setColourId(color.id)}
                            aria-pressed={chosenColourId === color.id}
                            className={cn(
                              'flex min-h-11 items-center gap-2 rounded-full border px-3 py-1.5 text-sm',
                              chosenColourId === color.id ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900' : 'border-slate-300 bg-white hover:bg-slate-50',
                            )}
                          >
                            <span className="size-5 rounded-full ring-1 ring-slate-300" style={{ background: color.rgb ? `#${color.rgb}` : '#e2e8f0' }} aria-hidden="true" />
                            {color.name}
                            <span className="text-xs text-slate-500">{note}</span>
                          </button>
                        ))}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      <label htmlFor="scan-other-colour">Another colour:</label>
                      <Select
                        id="scan-other-colour"
                        className="w-52"
                        value={chosenColourId ?? ''}
                        onChange={(e) => setColourId(e.target.value === '' ? null : Number(e.target.value))}
                      >
                        <option value="">Choose…</option>
                        {colors
                          .filter((c) => c.id !== 0)
                          .map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                      </Select>
                    </div>
                    {providerChips.length === 0 && <p className="mt-1 text-xs text-slate-500">Brickognize did not suggest a colour for this photo. Choose one.</p>}
                  </fieldset>
                )}

                <div className="flex flex-wrap items-end gap-3">
                  {!identifyOnly && (
                    <Field label="How many">
                      <Input inputMode="numeric" className="w-24" value={qty} onChange={(e) => setQty(e.target.value)} />
                    </Field>
                  )}
                  {!identifyOnly && (
                    <Button large disabled={!canAdd} onClick={() => add(true)}>
                      Add and scan next
                    </Button>
                  )}
                  <Button large className={identifyOnly ? 'w-full sm:w-auto' : undefined} variant={identifyOnly ? 'primary' : 'secondary'} disabled={!canAdd} onClick={() => add(false)}>
                    {identifyOnly ? 'Use this part' : 'Add'}
                  </Button>
                </div>
              </>
            )}
            <div>
              <Button variant="ghost" className="w-full sm:w-auto" onClick={openCamera}>
                Take another photo
              </Button>
            </div>
          </div>
        </div>
      )}

      <p className="mt-4 text-xs text-slate-500">
        The photo is shrunk, stripped of its location data, and sent to Brickognize to identify the part. BrickLoop does not keep it.
      </p>
    </Card>
  );
}
